import React, { useContext, useEffect } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import { LessonMode } from '../../../Constants';
import HtmlComponent from '../HtmlComponent';
import OptionalImage from '../OptionalImage';
import responseService from '../../services/ResponseService';
import textQuestionUtilsService from '../../services/TextQuestionUtilsService';
import UtilsService from '../../services/UtilsService';
import Guideline from '../tools/Guideline';
import { confirmDialog } from '../dialogs';

import '../../../css/HotText.scss';
import '../../../css/Accessibility.scss';
import useStyleEvents from '../../../hooks/useStyleEvents';
import PrintQuestionNumber from './PrintQuestionNumber';

const HotText = observer(({ lessonElementId }) => {
  // Initialize state and get question model and lessonState
  const {
    lessonManager,
    responseManager,
    questionFeedbackManager,
    toolbarManager
  } = useContext(MobXProviderContext);

  useStyleEvents(lessonElementId);

  let selectedIds = [];
  const model = lessonManager.getSlideModel(lessonElementId);
  const lessonElementState = responseManager.getLessonElementState(lessonElementId);

  // Function to get selection announcement text
  const getSelectionAnnouncement = () => {
    if (selectedIds.length === 0) {
      return 'No hot text selected';
    } else if (selectedIds.length === 1) {
      return '1 hot text item selected';
    } else {
      return `${selectedIds.length} hot text items selected`;
    }
  };

  const resetRanges = () => {
    const thisContainerId = `${lessonElementId}-item-view`;
    const thisContainer = document.getElementById(thisContainerId);
    if (thisContainer) {
      const rangeElements = thisContainer.getElementsByClassName('range');
      for (const elem of rangeElements) {
        elem.classList.remove('selected');
        elem.setAttribute('aria-checked', 'false');
        elem.classList.remove('correct');
        elem.classList.remove('incorrect');
        // Reset aria-label to unselected state
        const textContent = elem.textContent.trim();
        elem.setAttribute('aria-label', `Select text: "${textContent}"`);
      }
    }
  };

  const showStudentRanges = () => {
    resetRanges();
    for (const sa of lessonElementState.currentResponseAnswer.studentAnswers) {
      // query selector uses CSS selectors for querying the DOM, so ID's that start with
      // a number are not supported. CSS.escape solves this problem.
      const elem = document.querySelector(`[data-id=${ CSS.escape(sa) }]`);
      if (elem) {
        elem.classList.add('selected');
        elem.setAttribute('aria-checked', 'true');
        // Update aria-label to reflect selected state
        const textContent = elem.textContent.trim();
        elem.setAttribute('aria-label', `Selected text: "${textContent}"`);
      }
    }
  };

  const handleSelectedKey = function (event) {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault(); // Prevent default behavior for space key
      handleSelected(event);
    }
  };
  const handleSelected = function (event) {
    const feedbackState = questionFeedbackManager.getUiState(lessonElementId);
    if (feedbackState.showAnswerFeedback) {
      return;
    }
    // TODO using event.currentTarget instead of event.target becuase event.target would sometimes
    // be a child element of the clicked range.
    // const isThisSelected = event.target.classList.contains('selected');
    const selectedRangeElements = document.querySelectorAll(`#hot-text-body-${lessonElementId} .range.selected`);
    const isThisSelected = event.currentTarget.classList.contains('selected');

    if (model.correctAnswerCount === 1 && !isThisSelected) {
      const thisContainerId = `${lessonElementId}-item-view`;
      const thisContainer = document.getElementById(thisContainerId);
      if (thisContainer) {
        const rangeElements = thisContainer.getElementsByClassName('range');
        for (const elem of rangeElements) {
          elem.classList.remove('correct');
          elem.classList.remove('incorrect');
          elem.classList.remove('selected');
          elem.setAttribute('aria-checked', 'false');
          // Reset aria-label to unselected state
          const textContent = elem.textContent.trim();
          elem.setAttribute('aria-label', `Select text: "${textContent}"`);
        }
      }
      // event.target.classList.toggle('selected');
      event.currentTarget.classList.toggle('selected');
      // Update aria-checked and aria-label for the toggled element
      const isNowSelected = event.currentTarget.classList.contains('selected');
      event.currentTarget.setAttribute('aria-checked', isNowSelected ? 'true' : 'false');
      const textContent = event.currentTarget.textContent.trim();
      event.currentTarget.setAttribute('aria-label', isNowSelected ? `Selected text: "${textContent}"` : `Select text: "${textContent}"`);
    } else if ((selectedRangeElements.length === model.correctAnswerCount) &&
        // !event.target.classList.contains('selected')
        !event.currentTarget.classList.contains('selected')) {
      const dialogObject = {
        title: `${UtilsService.maxReached()}`,
        text: `${UtilsService.deselect2()}`,
        showCancelButton: false,
        confirmButtonText: `${UtilsService.confirmOk()}`
      };
      confirmDialog(dialogObject);
    } else {
      // event.target.classList.toggle('selected');
      event.currentTarget.classList.toggle('selected');
      // Update aria-checked and aria-label for the toggled element
      const isNowSelected = event.currentTarget.classList.contains('selected');
      event.currentTarget.setAttribute('aria-checked', isNowSelected ? 'true' : 'false');
      const textContent = event.currentTarget.textContent.trim();
      event.currentTarget.setAttribute('aria-label', isNowSelected ? `Selected text: "${textContent}"` : `Select text: "${textContent}"`);
    }

    selectedIds = [];
    const finalSelectedRangeElements = document.querySelectorAll(`#hot-text-body-${lessonElementId} .range.selected`);

    for (const elem of finalSelectedRangeElements) {
      selectedIds.push(elem.getAttribute('data-id'));
    }

    // Announce selection change
    const announcementElement = document.getElementById(`${lessonElementId}-hottext-announcement`);
    if (announcementElement) {
      announcementElement.textContent = getSelectionAnnouncement();
    }

    responseService.responseChangeHandler(selectedIds, lessonElementId);
  };

  useEffect(() => {
    const thisContainerId = `${lessonElementId}-item-view`;
    const thisContainer = document.getElementById(thisContainerId);
    const rangeElements = thisContainer.getElementsByClassName('range');

    Array.from(rangeElements).forEach((element) => {
      element.setAttribute('tabindex', '0');
      element.addEventListener('click', handleSelected);
      element.setAttribute('role', 'checkbox');
      element.setAttribute('aria-checked', 'false');
      element.addEventListener('keyup', handleSelectedKey);
      // Create a more descriptive aria-label that includes the text content and selection state
      const textContent = element.textContent.trim();
      element.setAttribute('aria-label', `Select text: "${textContent}"`);
    });

    if (lessonElementState.currentResponseAnswer.studentAnswers) {
      showStudentRanges();
      if (feedbackState && showAnswerFeedback) {
        textQuestionUtilsService.decorateFeedbackText(lessonElementState, lessonElementId);
      }
    }

    // Update announcement on load
    const announcementElement = document.getElementById(`${lessonElementId}-hottext-announcement`);
    if (announcementElement) {
      announcementElement.textContent = getSelectionAnnouncement();
    }

    return () => {
      const thisContainerId = `${lessonElementId}-item-view`;
      const thisContainer = document.getElementById(thisContainerId);

      if (thisContainer) {
        const rangeElements = thisContainer.getElementsByClassName('range');

        Array.from(rangeElements).forEach((element) => {
          element.removeEventListener('click', handleSelected);
          element.removeEventListener('keyup', handleSelectedKey);
        });
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonManager.currentLessonElementId, toolbarManager.isToolbarAnswerFeedbackActive]);

  const feedbackState = questionFeedbackManager.getUiState(lessonElementId);
  const showAnswerFeedback = (feedbackState) ? feedbackState.showAnswerFeedback : false;
  const showAnswers = (feedbackState) ? feedbackState.showAnswers : false;

  if (lessonElementState.currentResponseAnswer.studentAnswers && lessonElementState.currentResponseAnswer.studentAnswers.length > 0) {
    showStudentRanges();
    if (feedbackState && showAnswerFeedback) {
      textQuestionUtilsService.decorateFeedbackText(lessonElementState, lessonElementId);
    }
  }

  if (feedbackState && showAnswers) {
    textQuestionUtilsService.setCorrectAnswersText();
  }

  return (
    <>
      {toolbarManager.isGuidelineOpen && <Guideline lessonElementId={lessonElementId} />}
      <div aria-live="polite" aria-atomic="true" className="sr-only" id={`${lessonElementId}-hottext-announcement`}>
        {getSelectionAnnouncement()}
      </div>
      <div className='test-item-question' id={`test-item-question-${lessonElementId}`}>
        {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && <PrintQuestionNumber model={model} />}
        <HtmlComponent htmlStr={model.questionText} />
      </div>
      <OptionalImage model={model} runtime={true} />
      <div className='hotTextBody' id={`hot-text-body-${lessonElementId}`}>
        <HtmlComponent htmlStr={model.body} />
      </div>
    </>
  );
});
export default HotText;
